import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Steps,
  Tag,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SendOutlined,
  StopOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { Status } from '@/components';
import { PageEnum } from '@/enums/pageEnum';
import dayjs from '@/utils/dayjsConfig';
import { getTimeToString } from '@/utils/toolt';
import useMessageRedux from '@/store/message-redux';
import {
  caseWorkflow,
  type CaseActionParams,
  type CaseWorkflowAction,
} from '@/facilities/modules/case';
import { getAiHotlineSettings } from '@/facilities/modules/aiHotlineSettings';
import { parseIso } from './time';

export interface WorkflowStep {
  at: string;
  by?: number;
  byName?: string;
  action: string;
  from: string;
  to: string;
  event: string;
  note?: string;
}

export interface CaseNotification {
  at: string;
  channel: string;
  to?: string;
  text: string;
  status?: string;
}

interface BulkyDetails {
  appointmentDate?: string;
  appointmentWindow?: string;
  items?: string[];
  numItems?: number;
  remarks?: string;
  telephone?: string;
  bookingReference?: string;
  aiCallId?: number;
  callSid?: string;
  callerNumber?: string;
  contractor?: string;
  dispatchedAt?: string;
  dispatchedBy?: string;
  collectedAt?: string;
  collectedBy?: string;
  collectedItems?: string[];
  cancelReason?: string;
  cancelledAt?: string;
  closeNote?: string;
  history?: WorkflowStep[];
  notifications?: CaseNotification[];
}

export interface BulkyCaseData {
  id: number;
  caseNo: string;
  createdAt: number;
  closedAt?: number | null;
  status: string;
  priority: string;
  categoryFormat?: string;
  subcategoryFormat?: string;
  description?: string;
  complainant?: string;
  phone?: string;
  email?: string;
  complainantAddress?: string;
  buildingAddress?: string;
  buildingPostcode?: string;
  floor?: string;
  unit?: string;
  details?: BulkyDetails | null;
}

const WINDOWS = ['1 PM - 5 PM', '9 AM - 1 PM'];
const fmtIso = (iso?: string | null) =>
  iso && parseIso(iso).isValid() ? parseIso(iso).format('DD/MM/YYYY HH:mm') : '';

type ModalKind = Exclude<CaseWorkflowAction, 'reopen'> | null;

/**
 * Case-details tab for cases that are not lift complaints (bulky item removal bookings), with the
 * operator workflow: Open -> Dispatched -> Collected -> Closed, cancel from any open state, reopen.
 */
export default function BulkyCase({
  data,
  onChanged,
}: {
  data: BulkyCaseData;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const { message, modal } = useMessageRedux();
  const details = useMemo<BulkyDetails>(() => data.details || {}, [data.details]);
  const status = data.status;
  const [open, setOpen] = useState<ModalKind>(null);
  const [busy, setBusy] = useState(false);
  const [contractors, setContractors] = useState<string[]>([]);
  const [form] = Form.useForm<
    CaseActionParams & { date?: dayjs.Dayjs; at?: dayjs.Dayjs }
  >();

  const appointment = details.appointmentDate
    ? `${dayjs(details.appointmentDate).format('dddd, DD MMM YYYY')}${details.appointmentWindow ? ` · ${details.appointmentWindow}` : ''}`
    : '-';

  useEffect(() => {
    if (open !== 'dispatch') return;
    getAiHotlineSettings()
      .then((res) => setContractors(res.data?.body?.contractors || []))
      .catch(() => setContractors([]));
  }, [open]);

  const openModal = (kind: ModalKind) => {
    form.resetFields();
    form.setFieldsValue({
      contractor: details.contractor,
      date: details.appointmentDate ? dayjs(details.appointmentDate) : undefined,
      appointmentWindow: details.appointmentWindow || WINDOWS[0],
      at: dayjs(),
      collectedItems: details.items || [],
      notifyResident: true,
    });
    setOpen(kind);
  };

  const run = async (action: CaseWorkflowAction, params: CaseActionParams = {}) => {
    setBusy(true);
    try {
      await caseWorkflow(data.id, action, params);
      message.success(t('success'));
      setOpen(null);
      onChanged?.();
    } catch (error: unknown) {
      message.error(String((error as { detail?: string })?.detail || error));
    } finally {
      setBusy(false);
    }
  };

  const submitModal = async () => {
    const v = await form.validateFields();
    const params: CaseActionParams = {
      contractor: v.contractor,
      appointmentDate: v.date ? v.date.format('YYYY-MM-DD') : undefined,
      appointmentWindow: v.appointmentWindow,
      collectedAt: v.at ? v.at.toISOString() : undefined,
      collectedItems: v.collectedItems,
      note: v.note,
      notifyResident: v.notifyResident,
    };
    if (open) await run(open, params);
  };

  const confirmReopen = () =>
    modal.confirm({
      title: t('wf_reopenTitle'),
      onOk: () => run('reopen'),
    });

  // ---- progress
  const steps = useMemo(() => {
    const cancelled = status === 'CANCELLED';
    const current =
      status === 'CLOSED'
        ? 3
        : status === 'COLLECTED'
          ? 2
          : status === 'DISPATCHED'
            ? 1
            : 0;
    const items = [
      { title: t('wf_stepOpen'), description: getTimeToString(data.createdAt) },
      {
        title: t('wf_stepDispatched'),
        description: details.dispatchedAt
          ? `${fmtIso(details.dispatchedAt)}${details.contractor ? ` · ${details.contractor}` : ''}`
          : '',
      },
      { title: t('wf_stepCollected'), description: fmtIso(details.collectedAt) },
      {
        title: cancelled ? t('CANCELLED') : t('wf_stepClosed'),
        description: cancelled
          ? fmtIso(details.cancelledAt)
          : data.closedAt
            ? getTimeToString(data.closedAt)
            : '',
      },
    ];
    return { items, current: cancelled ? 3 : current, cancelled };
  }, [status, details, data.createdAt, data.closedAt, t]);

  const actions = (() => {
    switch (status) {
      case 'OPEN':
      case 'NEW':
        return (
          <>
            <Button
              danger
              type="text"
              onClick={() => openModal('cancel')}
              disabled={busy}
            >
              {t('wf_cancel')}
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => openModal('dispatch')}
              disabled={busy}
            >
              {t('wf_dispatch')}
            </Button>
          </>
        );
      case 'DISPATCHED':
        return (
          <>
            <Button
              danger
              type="text"
              onClick={() => openModal('cancel')}
              disabled={busy}
            >
              {t('wf_cancel')}
            </Button>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => openModal('reschedule')}
              disabled={busy}
            >
              {t('wf_reschedule')}
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => openModal('collected')}
              disabled={busy}
            >
              {t('wf_markCollected')}
            </Button>
          </>
        );
      case 'COLLECTED':
        return (
          <>
            <Button icon={<ReloadOutlined />} onClick={confirmReopen} disabled={busy}>
              {t('wf_reopen')}
            </Button>
            <Button
              type="primary"
              icon={<CloseCircleOutlined />}
              onClick={() => openModal('close')}
              disabled={busy}
            >
              {t('wf_close')}
            </Button>
          </>
        );
      default:
        return (
          <Button icon={<ReloadOutlined />} onClick={confirmReopen} disabled={busy}>
            {t('wf_reopen')}
          </Button>
        );
    }
  })();

  const modalTitle: Record<Exclude<ModalKind, null>, string> = {
    dispatch: t('wf_dispatchTitle'),
    reschedule: t('wf_rescheduleTitle'),
    collected: t('wf_collectedTitle'),
    close: t('wf_closeTitle'),
    cancel: t('wf_cancelTitle'),
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="bg-[#fff] dark:bg-[#141414] p-4">
        <div className="pb-4 mb-5 border-b-[1px] leading-5 border-[#E8E9ED] dark:border-[#ffffff21] flex items-center gap-6 flex-wrap">
          <span className="text-[20px] font-bold">
            {t('caseID')}: {data.caseNo}
          </span>
          <span>
            {t('createdDate')}: {getTimeToString(data.createdAt)}
          </span>
          <Status status={data.status} />
          <Status status={data.priority} />
          <Space className="ml-auto" data-testid="case-actions">
            {actions}
          </Space>
        </div>

        <div className="text-[20px] font-medium mb-4">{t('wf_progress')}</div>
        {steps.cancelled && (
          <Alert
            className="mb-4"
            type="error"
            showIcon
            icon={<StopOutlined />}
            message={t('wf_cancelledBanner')}
            description={details.cancelReason}
          />
        )}
        <Steps
          size="small"
          current={steps.current}
          status={steps.cancelled ? 'error' : status === 'CLOSED' ? 'finish' : 'process'}
          items={steps.items}
          className="mb-6"
          data-testid="case-steps"
        />

        <div className="text-[20px] font-medium mb-4">{t('hotline_bookingDetails')}</div>
        <Descriptions
          column={2}
          bordered
          size="middle"
          items={[
            { key: 'cat', label: t('category'), children: data.categoryFormat },
            { key: 'sub', label: t('subcategory'), children: data.subcategoryFormat },
            { key: 'date', label: t('hotline_appointment'), children: appointment },
            {
              key: 'ref',
              label: t('hotline_bookingRef'),
              children: (
                <span className="font-mono">{details.bookingReference || '-'}</span>
              ),
            },
            {
              key: 'contractor',
              label: t('wf_contractor'),
              children: details.contractor || '-',
            },
            {
              key: 'collected',
              label: t('wf_collectedAt'),
              children: details.collectedAt ? fmtIso(details.collectedAt) : '-',
            },
            {
              key: 'items',
              label: t('hotline_items'),
              span: 2,
              children: (
                <div className="flex flex-wrap gap-1">
                  {(details.items || []).map((item, i) => (
                    <Tag
                      key={i}
                      color={
                        details.collectedItems?.includes(item) ? 'success' : undefined
                      }
                    >
                      {item}
                    </Tag>
                  ))}
                  {!details.items?.length && '-'}
                </div>
              ),
            },
            {
              key: 'remarks',
              label: t('remarks'),
              span: 2,
              children: details.remarks || '-',
            },
            { key: 'desc', label: t('description'), span: 2, children: data.description },
            ...(details.closeNote
              ? [
                  {
                    key: 'closeNote',
                    label: t('wf_closeNote'),
                    span: 2,
                    children: details.closeNote,
                  },
                ]
              : []),
          ]}
        />
      </div>

      <div className="bg-[#fff] dark:bg-[#141414] p-4">
        <div className="text-[20px] font-medium mb-4">{t('complainant')}</div>
        <Descriptions
          column={2}
          bordered
          size="middle"
          items={[
            { key: 'name', label: t('name'), children: data.complainant || '-' },
            { key: 'phone', label: t('handphone'), children: data.phone || '-' },
            { key: 'email', label: t('email'), children: data.email || '-' },
            { key: 'tel', label: t('telephone'), children: details.telephone || '-' },
            {
              key: 'addr',
              label: t('address'),
              span: 2,
              children: data.complainantAddress || data.buildingAddress || '-',
            },
            {
              key: 'postal',
              label: t('postalCode'),
              children: data.buildingPostcode || '-',
            },
            {
              key: 'unit',
              label: `${t('floor')} / ${t('unit')}`,
              children: data.floor && data.unit ? `#${data.floor}-${data.unit}` : '-',
            },
          ]}
        />
      </div>

      {details.aiCallId && (
        <div className="bg-[#fff] dark:bg-[#141414] p-4">
          <div className="text-[20px] font-medium mb-2">{t('ai_hotline')}</div>
          <p>
            {t('hotline_createdFromCall')}{' '}
            <Link to={`${PageEnum.AI_HOTLINE}?id=${details.aiCallId}`}>
              #{details.aiCallId}
            </Link>
            {details.callSid && (
              <span className="ml-2 font-mono text-[12px] text-[#00000073]">
                {details.callSid}
              </span>
            )}
          </p>
        </div>
      )}

      <Modal
        open={open !== null}
        title={open ? modalTitle[open] : ''}
        okText={
          open ? t(`wf_${open === 'collected' ? 'markCollected' : open}`) : t('save')
        }
        okButtonProps={{ danger: open === 'cancel', loading: busy }}
        onOk={submitModal}
        onCancel={() => setOpen(null)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt-4">
          {open === 'dispatch' && (
            <Form.Item
              name="contractor"
              label={t('wf_contractor')}
              rules={[{ required: true, message: t('wf_contractorRequired') }]}
            >
              <Select
                showSearch
                options={contractors.map((c) => ({ value: c, label: c }))}
                placeholder={t('wf_contractorRequired')}
                data-testid="wf-contractor"
              />
            </Form.Item>
          )}
          {(open === 'dispatch' || open === 'reschedule') && (
            <>
              <Form.Item
                name="date"
                label={t('wf_collectionDate')}
                rules={[{ required: true }]}
              >
                <DatePicker
                  className="w-full"
                  format="DD-MM-YYYY"
                  disabledDate={(d) => d.day() === 0 || d.day() === 6}
                />
              </Form.Item>
              <Form.Item name="appointmentWindow" label={t('wf_window')}>
                <Select options={WINDOWS.map((w) => ({ value: w, label: w }))} />
              </Form.Item>
              <Form.Item name="notifyResident" valuePropName="checked">
                <Checkbox>{t('wf_notify')}</Checkbox>
              </Form.Item>
            </>
          )}
          {open === 'collected' && (
            <>
              <Form.Item
                name="at"
                label={t('wf_collectedAt')}
                rules={[{ required: true }]}
              >
                <DatePicker showTime className="w-full" format="DD-MM-YYYY HH:mm" />
              </Form.Item>
              <Form.Item name="collectedItems" label={t('wf_collectedItems')}>
                <Checkbox.Group
                  options={(details.items || []).map((i) => ({ label: i, value: i }))}
                />
              </Form.Item>
            </>
          )}
          {open === 'cancel' ? (
            <Form.Item
              name="note"
              label={t('wf_cancelReason')}
              rules={[{ required: true, message: t('wf_cancelReasonRequired') }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          ) : (
            <Form.Item
              name="note"
              label={open === 'close' ? t('wf_closeNote') : t('wf_note')}
            >
              <Input.TextArea rows={2} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
